import { useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { MessageCircle, X, SendHorizontal, Loader2 } from 'lucide-react'
import { useWebChatStore } from '@/stores'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

const FAB_SIZE = 64
const FAB_MARGIN = 16
const CLOSED_SIZE = FAB_SIZE + FAB_MARGIN * 2
const PANEL_WIDTH = 380
const PANEL_HEIGHT = 620

const RESIZE_MESSAGE_TYPE = 'hoge-webchat:resize'

function notifyResize(open: boolean) {
  if (window.parent === window) return
  window.parent.postMessage(
    {
      type: RESIZE_MESSAGE_TYPE,
      open,
      width: open ? PANEL_WIDTH : CLOSED_SIZE,
      height: open ? PANEL_HEIGHT : CLOSED_SIZE,
    },
    '*'
  )
}

const formatTime = (dateString?: string) => {
  if (!dateString) return ''
  return new Intl.DateTimeFormat('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(dateString))
}

const WebChatWidget = observer(() => {
  const store = useWebChatStore()
  const bottomRef = useRef<HTMLDivElement>(null)

  // Keep the host iframe in sync with the open/closed state.
  useEffect(() => {
    notifyResize(store.open)
  }, [store.open])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [store.messages.length, store.open])

  useEffect(() => {
    return () => store.disconnect()
  }, [store])

  const handleSubmit = () => {
    void store.sendMessage()
  }

  if (!store.open) {
    return (
      <div
        className="flex h-screen w-screen items-end justify-end"
        style={{ padding: FAB_MARGIN }}
      >
        <button
          type="button"
          aria-label="チャットを開く"
          onClick={() => store.toggle()}
          className="flex items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105"
          style={{ width: FAB_SIZE, height: FAB_SIZE }}
        >
          <MessageCircle className="h-7 w-7" />
        </button>
      </div>
    )
  }

  return (
    <div className="flex h-screen w-screen items-end justify-end" style={{ padding: 0 }}>
      <div
        className="flex h-full w-full flex-col overflow-hidden rounded-none border bg-background shadow-2xl sm:rounded-xl"
      >
        <header className="flex items-center justify-between border-b bg-primary px-4 py-3 text-primary-foreground">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">
              {store.tenantName || 'チャットサポート'}
            </div>
            <div className="text-xs opacity-80">お気軽にお問い合わせください</div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="閉じる"
            className="text-primary-foreground hover:bg-white/15 hover:text-primary-foreground"
            onClick={() => store.closeWidget()}
          >
            <X className="h-5 w-5" />
          </Button>
        </header>

        <div className="flex-1 overflow-auto bg-muted/30 px-4 py-3">
          {store.status === 'loading' && (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              読み込み中...
            </div>
          )}

          {store.status === 'disabled' && (
            <div className="flex h-full items-center justify-center px-4 text-center text-sm text-muted-foreground">
              現在、Webチャットは受け付けていません。
            </div>
          )}

          {store.status === 'error' && (
            <div className="flex h-full items-center justify-center px-4 text-center text-sm text-muted-foreground">
              接続に失敗しました。時間をおいて再度お試しください。
            </div>
          )}

          {store.status === 'ready' && store.messages.length === 0 && (
            <div className="flex h-full items-center justify-center px-4 text-center text-sm text-muted-foreground">
              メッセージを送信してチャットを開始してください。
            </div>
          )}

          {store.status === 'ready' &&
            store.messages.map((message, index) => {
              const isSelf = !message.user
              return (
                <div
                  key={message.id || index}
                  className={cn('mb-3 flex flex-col', isSelf ? 'items-end' : 'items-start')}
                >
                  <div
                    className={cn(
                      'max-w-[80%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm',
                      isSelf
                        ? 'rounded-br-sm bg-primary text-primary-foreground'
                        : 'rounded-bl-sm bg-background text-foreground shadow-sm'
                    )}
                  >
                    {message.type === 'image' && message.image_url ? (
                      <img
                        src={message.image_url}
                        alt="添付画像"
                        className="max-h-64 max-w-full rounded-md"
                      />
                    ) : (
                      message.body
                    )}
                  </div>
                  <span className="mt-1 px-1 text-[10px] text-muted-foreground">
                    {formatTime(message.created)}
                  </span>
                </div>
              )
            })}
          <div ref={bottomRef} />
        </div>

        <div className="flex items-center gap-2 border-t p-3">
          <Input
            value={store.input}
            onChange={(e) => store.setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing && !e.shiftKey) {
                e.preventDefault()
                handleSubmit()
              }
            }}
            placeholder="メッセージを入力..."
            disabled={store.status !== 'ready'}
          />
          <Button
            type="button"
            size="icon"
            aria-label="送信"
            onClick={handleSubmit}
            disabled={store.status !== 'ready' || store.sending || !store.input.trim()}
          >
            {store.sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <SendHorizontal className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  )
})

export default WebChatWidget
